import { useState } from 'react'
import { Link, router } from '@inertiajs/react'

import { Button } from '@atoms'
import { PageLayout } from '@components/layouts/PageLayout'
import { PageHeaderNav } from '@molecules/PageHeaderNav'
import type { SettingsHeader } from '@components/layouts/PageHeaderIsland'
import EmptyState from '@components/molecules/EmptyState'
import useMutation from '@hooks/useGQLMutation'
import { usePartialPoll } from '@hooks/usePartialPoll'
import { toast } from '@stores/toastStore'
import { scopedTranslation } from '@utils/I18n'

import { ExportSupportersCsvDocument } from '@frontend/graphql/mutations/exportSupportersCsv.generated'

import { ExportHistoryTable, type ExportRecord } from '../features/export-supporters/ExportHistoryTable'

const t = scopedTranslation('supporters_export')

const POLL_INTERVAL_MS = 4000
const RELOAD_KEYS = ['exports', 'hasOwnPendingExport']
const SUPPORT_EMAIL = 'info@supporterbase.com'

function PendingExportPoller() {
  usePartialPoll(POLL_INTERVAL_MS, RELOAD_KEYS)

  return null
}

export type SupportersExportProps = {
  userEmail: string
  supporterCount: number
  csvColumns: string[]
  exports: ExportRecord[]
  hasOwnPendingExport: boolean
  settingsHeader: SettingsHeader
}

export default function SupportersExport({
  userEmail,
  supporterCount,
  csvColumns,
  exports,
  hasOwnPendingExport,
  settingsHeader: { heading, subtitle, icon, pageTabs },
}: SupportersExportProps) {
  const [isRequesting, setIsRequesting] = useState(false)
  const { mutate } = useMutation(ExportSupportersCsvDocument)

  const hasSupporters = supporterCount > 0
  const isAnyExportGenerating = exports.some((record) => record.status === 'pending')

  const notifyFailure = () =>
    toast.error(t('alerts.error.title'), {
      description: t('alerts.error.body', { support_email: SUPPORT_EMAIL }),
    })

  const requestExport = () => {
    setIsRequesting(true)

    mutate(
      {},
      {
        onSuccess: (response) => {
          if (!response?.exportSupportersCsv?.success) {
            notifyFailure()
            return
          }

          toast.success(t('alerts.success.title'), {
            description: t('alerts.success.body', { email: userEmail }),
          })
          router.reload({ only: RELOAD_KEYS })
        },
        onError: notifyFailure,
        onSettled: () => setIsRequesting(false),
      }
    )
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header
        heading={heading}
        icon={icon}
        subtitle={subtitle}
        nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={Link} />}
      />
      <PageLayout.Body>
        {isAnyExportGenerating && <PendingExportPoller />}

        <p role="status" className="tw:sr-only">
          {hasOwnPendingExport ? t('request.in_progress') : ''}
        </p>

        <div className="tw:flex tw:flex-col tw:gap-6">
          <section className="tw:flex tw:w-full tw:flex-col tw:gap-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:shadow-xs tw:sm:p-6">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <h2 className="tw:m-0 tw:text-lg tw:font-bold tw:text-gray-cool-900">{t('request.heading')}</h2>

              {hasSupporters && (
                <p className="tw:m-0 tw:text-sm tw:text-gray-cool-600">
                  {t('request.summary', { count: supporterCount, email: userEmail })}
                </p>
              )}
            </div>

            {hasSupporters ? (
              <>
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <h3 className="tw:m-0 tw:text-sm tw:font-semibold tw:text-gray-cool-700">
                    {t('request.columns_label')}
                  </h3>
                  <ul className="tw:m-0 tw:flex tw:flex-wrap tw:gap-2 tw:p-0">
                    {csvColumns.map((column) => (
                      <li
                        key={column}
                        className="tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:px-2 tw:py-1 tw:font-mono tw:text-xs tw:text-gray-cool-700"
                      >
                        {column}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                  <Button
                    onClick={requestExport}
                    disabled={isRequesting || hasOwnPendingExport}
                    leadingIcon="file-download-03"
                  >
                    {isRequesting ? t('request.submit_loading') : t('request.submit')}
                  </Button>
                  {hasOwnPendingExport && (
                    <span className="tw:text-sm tw:text-gray-cool-600">{t('request.in_progress')}</span>
                  )}
                </div>
              </>
            ) : (
              <EmptyState
                className="tw:border-0"
                iconType="users-01"
                headline={t('empty_supporters.headline')}
                subtext={t('empty_supporters.subtext')}
              />
            )}
          </section>

          <section className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <h2 className="tw:m-0 tw:text-lg tw:font-bold tw:text-gray-cool-900">{t('history.heading')}</h2>
              <p className="tw:m-0 tw:text-sm tw:text-gray-cool-600">{t('history.subtitle')}</p>
            </div>
            <ExportHistoryTable exports={exports} />
          </section>
        </div>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
